import React, { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  FileText,
  TrendingUp,
  Users,
  Zap,
  BarChart2,
  Clock,
  Tag,
  Repeat,
  RefreshCw,
  ArrowUpRight,
  ArrowDownRight,
  ArrowUp,
  ArrowDown,
  ArrowUpDown,
} from "lucide-react";
import "./Admin.scss";
import axiosInstance from "../../axiosInstace";
import FormBuilder from "../../components/FormBuilder/FormBuilder";
import { toast } from "react-toastify";
import LoadingScreen from "../../LoadingScreen/LoadingScreen";
import { useAuth } from "../../contexts/AuthContext";
import {
  AreaChart,
  Area,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  CartesianGrid,
  Legend,
} from "recharts";
import ButtonWithIcon from "../../components/ButtonwithIcon/ButtonWithIcon";

const DONUT_COLORS = ["#017bfe", "#10b981", "#f59e0b", "#e11d48"];
const MONTH_ORDER = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

// ─── ISO-8601 duration helpers (e.g. "PT3S", "PT10M", "PT1H30M") ─────────────
const parseDuration = (iso) => {
  if (!iso) return { h: 0, m: 0, s: 0 };
  const match = /^PT(?:(\d+)H)?(?:(\d+)M)?(?:(\d+)S)?$/.exec(iso);
  if (!match) return { h: 0, m: 0, s: 0 };
  const [, h, m, s] = match;
  return { h: Number(h || 0), m: Number(m || 0), s: Number(s || 0) };
};

const durationToSeconds = (iso) => {
  const { h, m, s } = parseDuration(iso);
  return h * 3600 + m * 60 + s;
};

const formatDuration = (iso) => {
  if (!iso) return "—";
  const { h, m, s } = parseDuration(iso);
  const parts = [];
  if (h) parts.push(`${h}h`);
  if (m) parts.push(`${m}m`);
  if (s) parts.push(`${s}s`);
  return parts.length ? parts.join(" ") : "0s";
};

const formatDurationShort = (totalSeconds) => {
  if (!totalSeconds) return "—";
  const m = Math.floor(totalSeconds / 60);
  const s = totalSeconds % 60;
  if (m) return `${m}m ${s}s`;
  return `${s}s`;
};

// ─── Attempt type helper ──────────────────────────────────────────────────
const formatAttempts = (test) => {
  if (test.testAttemptType === "UNLIMITED") return "Unlimited";
  if (!test.maxAttempts) return "—";
  return `${test.maxAttempts} attempt${test.maxAttempts > 1 ? "s" : ""}`;
};

// ─── Aggregate monthly trend across all tests ────────────────────────────────
const aggregateMonthlyTrend = (tests) => {
  const map = {};
  tests.forEach((t) => {
    (t.monthlyTrend || []).forEach(({ month, completions, participants }) => {
      if (!map[month]) map[month] = { month, completions: 0, participants: 0 };
      map[month].completions += completions || 0;
      map[month].participants += participants || 0;
    });
  });
  return MONTH_ORDER.filter((m) => map[m]).map((m) => map[m]);
};

// ─── Build sparkline data from aggregated monthly trend ──────────────────────
// Keeps all months (including zeros) so recharts draws a line, not a dot.
// When all values are zero, falls back to a scalar-seeded shape so the
// sparkline still renders meaningfully.
const buildSparkData = (tests, key = "completions", fallbackValue = 0) => {
  const map = {};
  MONTH_ORDER.forEach((m) => {
    map[m] = 0;
  }); // seed every month with 0
  tests.forEach((t) => {
    (t.monthlyTrend || []).forEach(({ month, ...rest }) => {
      if (map[month] !== undefined) map[month] += rest[key] || 0;
    });
  });
  const vals = MONTH_ORDER.slice(0, 6).map((m) => ({ v: map[m] }));
  const allZero = vals.every((d) => d.v === 0);
  if (allZero && fallbackValue > 0) {
    // Distribute the scalar across 6 positions with a gentle curve shape
    const base = fallbackValue;
    return [0.4, 0.55, 0.6, 0.75, 0.85, 1].map((f) => ({
      v: Math.round(base * f),
    }));
  }
  return vals;
};

// ─── Mini Sparkline ──────────────────────────────────────────────────────────
const Sparkline = ({ data, color }) => (
  <ResponsiveContainer width="99%" height={28}>
    <AreaChart data={data} margin={{ top: 2, bottom: 2, left: 0, right: 0 }}>
      <defs>
        <linearGradient
          id={`sg-${color.replace("#", "")}`}
          x1="0"
          y1="0"
          x2="0"
          y2="1"
        >
          <stop offset="5%" stopColor={color} stopOpacity={0.3} />
          <stop offset="95%" stopColor={color} stopOpacity={0} />
        </linearGradient>
      </defs>
      <Area
        type="monotone"
        dataKey="v"
        stroke={color}
        strokeWidth={2}
        fill={`url(#sg-${color.replace("#", "")})`}
        dot={false}
        isAnimationActive={false}
      />
    </AreaChart>
  </ResponsiveContainer>
);

// ─── KPI Card — compact, matches TestManagement's mini-stat sizing ──────────
const KpiCard = ({
  icon: Icon,
  label,
  value,
  sub,
  trend,
  trendUp,
  color,
  sparkData: sd,
}) => (
  <div className={`kpi-card kpi-card--${color}`}>
    <div className="kpi-card__top">
      <div className={`kpi-card__icon kpi-card__icon--${color}`}>
        <Icon size={14} />
      </div>
      <span className={`kpi-card__trend ${trendUp ? "up" : "down"}`}>
        {trendUp ? <ArrowUpRight size={11} /> : <ArrowDownRight size={11} />}
        {trend}
      </span>
    </div>
    <div className="kpi-card__value">{value}</div>
    <div className="kpi-card__label">{label}</div>
    <div className="kpi-card__sub">{sub}</div>
    <div className="kpi-card__spark">
      <Sparkline
        data={sd}
        color={
          color === "indigo"
            ? "#017bfe"
            : color === "emerald"
              ? "#10b981"
              : color === "amber"
                ? "#f59e0b"
                : color === "rose"
                  ? "#e11d48"
                  : color === "cyan"
                    ? "#06b6d4"
                    : "#8b5cf6"
        }
      />
    </div>
  </div>
);

// ─── Status Badge ────────────────────────────────────────────────────────────
const StatusBadge = ({ status }) => {
  if (status === "expired") {
    return <span className="status-badge expired">Expired</span>;
  }

  if (status === "inactive") {
    return <span className="status-badge inactive">Inactive</span>;
  }

  return <span className="status-badge active">Active</span>;
};

// ─── Score Bar ───────────────────────────────────────────────────────────────
const ScoreBar = ({ value, max }) => {
  const pct = max > 0 ? Math.round((value / max) * 100) : 0;
  return (
    <div className="score-bar">
      <div className="score-bar__track">
        <div className="score-bar__fill" style={{ width: `${pct}%` }} />
      </div>
      <span className="score-bar__label">{pct}%</span>
    </div>
  );
};

// ─── Custom Tooltip ──────────────────────────────────────────────────────────
const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="chart-tooltip">
      <p className="chart-tooltip__label">{label}</p>
      {payload.map((p, i) => (
        <p key={i} style={{ color: p.color }} className="chart-tooltip__val">
          {p.name}: <strong>{p.value}</strong>
        </p>
      ))}
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════════════════════════════════════════
const Admin = ({ userRole = "ROLE_ADMIN" }) => {
  const isUserOnly = userRole === "ROLE_USER";
  const { isCreator } = useAuth();
  const [tests, setTests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchParams, setSearchParams] = useSearchParams();
  const viewParam = searchParams.get("view");
  const showFormBuilder = viewParam === "create" || viewParam === "edit";
  const formBuilderMode = viewParam === "edit" ? "edit" : "create";
  const selectedTestId = searchParams.get("testId");
  const [sortConfig, setSortConfig] = useState({
    key: "resultCount",
    direction: "desc",
  });
  const [highlightedTestId, setHighlightedTestId] = useState(null);
  const rowRefs = useRef({});

  useEffect(() => {
    fetchDashboardStats();
  }, []);

  // ── Highlight + scroll to a test row when arriving via a "View Results"
  //    redirect (?testId=...) rather than the edit/create form flow ──────────
  useEffect(() => {
    if (showFormBuilder || tests.length === 0) return;
    const targetId = searchParams.get("testId");
    if (!targetId) return;

    setHighlightedTestId(targetId);
    const row = rowRefs.current[targetId];
    if (row) {
      row.scrollIntoView({ behavior: "smooth", block: "center" });
    }

    const timer = setTimeout(() => {
      setHighlightedTestId(null);
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.delete("testId");
          return next;
        },
        { replace: true },
      );
    }, 2500);

    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tests, showFormBuilder]);

  const fetchDashboardStats = async () => {
    setLoading(true);
    try {
      const [singleTest, allTestDetails] = await Promise.all([
        axiosInstance.get(
          `${process.env.REACT_APP_API_USER_URL}/dashboard/singletestresponse`,
        ),
        axiosInstance.get(`${process.env.REACT_APP_API_USER_URL}/tests/getall`),
      ]);
      const testStats = singleTest.data?.data || [];
      const testDetails = allTestDetails.data?.data || [];
      const map = testDetails.reduce((a, t) => {
        a[t.testId] = t;
        return a;
      }, {});
      const now = new Date();

      const merged = testStats.map((stat) => {
        const d = map[stat.testId] || {};

        const expirationDate = d.linkExpiryDate || stat.linkExpiryDate || null;

        const exp = expirationDate ? new Date(expirationDate) : null;

        const isExpired = exp ? exp < now : false;

        const daysLeft = exp ? Math.ceil((exp - now) / 86400000) : null;

        const canAttempt = d.canAttempt ?? stat.canAttempt ?? false;

        // Same status logic as Test Management
        const status = isExpired
          ? "expired"
          : canAttempt
            ? "active"
            : "inactive";

        return {
          ...stat,
          title: d.testTitle || stat.testTitle || "Untitled",
          expirationDate,
          description: d.description || stat.description || "",
          totalScore: d.totalScore || stat.totalScore || 0,
          monthlyTrend: stat.monthlyTrend || d.monthlyTrend || [],
          testDuration: stat.testDuration || d.testDuration || null,
          testAttemptType: stat.testAttemptType || d.testAttemptType || null,
          maxAttempts: stat.maxAttempts ?? d.maxAttempts ?? null,
          testCategoryType: d.testCategoryType || "General",
          usedAttempts: d.usedAttempts ?? null,
          remainingAttempts: d.remainingAttempts ?? null,
          canAttempt,
          // Unified status
          status,
          isExpired,
          // Keep this only as an additional expiry warning
          daysLeft,
          isExpiringSoon:
            !isExpired && daysLeft !== null && daysLeft > 0 && daysLeft <= 7,
        };
      });
      setTests(merged);
    } catch (err) {
      console.error(err);
      toast.error("Failed to fetch dashboard data.");
    } finally {
      setLoading(false);
    }
  };

  // ── Derived KPI values ─────────────────────────────────────────────────────
  const totalParticipants = tests.reduce((s, t) => s + (t.resultCount || 0), 0);
  const activeCount = tests.filter((t) => t.status === "active").length;
  const inactiveCount = tests.filter((t) => t.status === "inactive").length;
  const expiredCount = tests.filter((t) => t.status === "expired").length;
  const expiringSoonCount = tests.filter((t) => t.isExpiringSoon).length;
  const avgRate = tests.length
    ? Math.round(
        tests.reduce(
          (s, t) =>
            s + (t.totalScore > 0 ? (t.averageScore / t.totalScore) * 100 : 0),
          0,
        ) / tests.length,
      )
    : 0;
  const avgDurationSeconds = tests.length
    ? Math.round(
        tests.reduce((s, t) => s + durationToSeconds(t.testDuration), 0) /
          tests.length,
      )
    : 0;

  // ── Real aggregated data ───────────────────────────────────────────────────
  const aggregatedTrend = aggregateMonthlyTrend(tests);
  const sparkCompletions = buildSparkData(
    tests,
    "completions",
    totalParticipants,
  );
  const sparkParticipants = buildSparkData(
    tests,
    "participants",
    totalParticipants,
  );

  // Extracts a comparable value for each sortable column.
  const getSortValue = (test, key) => {
    switch (key) {
      case "title":
        return (test.title || "").toLowerCase();
      case "resultCount":
        return test.resultCount ?? 0;
      case "averageScore":
        return test.averageScore ?? 0;
      case "expirationDate":
        return test.expirationDate
          ? new Date(test.expirationDate).getTime()
          : 0;
      case "testId":
      default:
        return test.testId ?? 0;
    }
  };

  const toggleSort = (key) => {
    setSortConfig((prev) => {
      if (prev.key === key) {
        return { key, direction: prev.direction === "asc" ? "desc" : "asc" };
      }
      return { key, direction: "asc" };
    });
  };

  const renderSortIcon = (key) => {
    if (sortConfig.key !== key) {
      return <ArrowUpDown size={12} className="sort-icon" />;
    }
    return sortConfig.direction === "asc" ? (
      <ArrowUp size={12} className="sort-icon active" />
    ) : (
      <ArrowDown size={12} className="sort-icon active" />
    );
  };

  const sortedTests = [...tests].sort((a, b) => {
    const aValue = getSortValue(a, sortConfig.key);
    const bValue = getSortValue(b, sortConfig.key);
    if (aValue < bValue) return sortConfig.direction === "asc" ? -1 : 1;
    if (aValue > bValue) return sortConfig.direction === "asc" ? 1 : -1;
    return 0;
  });

  // ── Donut data ─────────────────────────────────────────────────────────────
  const donutData = tests.slice(0, 4).map((t) => ({
    name: t.title.length > 20 ? t.title.slice(0, 18) + "…" : t.title,
    value: t.resultCount || 0,
  }));

  if (loading) return <LoadingScreen />;

  if (showFormBuilder)
    return (
      <FormBuilder
        mode={formBuilderMode}
        testId={selectedTestId}
        userRole={userRole}
        onBack={() => {
          setSearchParams({});
          fetchDashboardStats();
        }}
      />
    );

  return (
    <div className="adm">
      {/* ── TOP HEADER — matches TestManagement's dashboard-header ─────────── */}
      <header className="dashboard-header">
        <div className="header-content">
          <h1>{isUserOnly ? "My Tests" : "Dashboard"}</h1>
          <p>
            {isUserOnly
              ? "Create and manage your own assessments"
              : "Assessment analytics & performance overview"}
          </p>
        </div>

        <div className="header-actions">
          <button
            className="adm__icon-btn outline"
            title="Refresh"
            onClick={fetchDashboardStats}
          >
            <RefreshCw size={14} />
          </button>
          {isCreator && (
            <ButtonWithIcon
              icon="material-symbols:add-rounded"
              className="submit-button"
              onClick={() => setSearchParams({ view: "create" })}
              text="New Assessment"
            />
          )}
        </div>
      </header>

      {/* ── KPI ROW ──────────────────────────────────────────────────────────── */}
      {!isUserOnly && (
        <section className="adm__kpi-row">
          <KpiCard
            icon={FileText}
            label="Total Assessments"
            value={tests.length}
            sub="All time"
            trend={`${activeCount} active`}
            trendUp={activeCount > 0}
            color="indigo"
            sparkData={sparkCompletions}
          />
          <KpiCard
            icon={Users}
            label="Total Participants"
            value={totalParticipants}
            sub="Across all tests"
            trend={`${totalParticipants} responses`}
            trendUp={totalParticipants > 0}
            color="cyan"
            sparkData={sparkParticipants}
          />
          <KpiCard
            icon={Zap}
            label="Active Assessments"
            value={activeCount}
            sub={`${inactiveCount} inactive · ${expiredCount} expired`}
            trend={
              inactiveCount > 0 ? `${inactiveCount} inactive` : "All active"
            }
            trendUp={inactiveCount === 0}
            color="emerald"
            sparkData={sparkCompletions}
          />
          <KpiCard
            icon={BarChart2}
            label="Avg Completion Rate"
            value={`${avgRate}%`}
            sub="Avg score vs max"
            trend={`${avgRate}% avg`}
            trendUp={avgRate >= 50}
            color="purple"
            sparkData={sparkCompletions}
          />
          <KpiCard
            icon={Clock}
            label="Avg Duration"
            value={formatDurationShort(avgDurationSeconds)}
            sub="Per assessment"
            trend="Per attempt"
            trendUp
            color="amber"
            sparkData={sparkParticipants}
          />
          <KpiCard
            icon={TrendingUp}
            label="Expiring Soon"
            value={expiringSoonCount}
            sub="Within 7 days"
            trend={expiringSoonCount > 0 ? "Action needed" : "All good"}
            trendUp={expiringSoonCount === 0}
            color="rose"
            sparkData={sparkCompletions}
          />
        </section>
      )}

      {/* ── CHARTS ROW ────────────────────────────────────────────────────── */}
      {!isUserOnly && (
        <section className="adm__mid-row">
          {/* Area chart — real aggregated monthly trend */}
          <div className="adm__panel adm__panel--trend">
            <div className="adm__panel-header">
              <div>
                <h3>Engagement Trends</h3>
                <p>Completions & participants over time</p>
              </div>
              <span className="adm__period-badge">Monthly</span>
            </div>
            <ResponsiveContainer width="100%" height={190}>
              <AreaChart
                data={aggregatedTrend}
                margin={{ top: 10, right: 10, bottom: 0, left: -20 }}
              >
                <defs>
                  <linearGradient id="gComp" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#017bfe" stopOpacity={0.35} />
                    <stop offset="95%" stopColor="#017bfe" stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="gPart" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#10b981" stopOpacity={0.25} />
                    <stop offset="95%" stopColor="#10b981" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid
                  strokeDasharray="3 3"
                  stroke="rgba(0,0,0,0.05)"
                />
                <XAxis
                  dataKey="month"
                  tick={{ fontSize: 11, fill: "#94a3b8" }}
                  axisLine={false}
                  tickLine={false}
                />
                <YAxis
                  tick={{ fontSize: 11, fill: "#94a3b8" }}
                  axisLine={false}
                  tickLine={false}
                />
                <Tooltip content={<CustomTooltip />} />
                <Legend
                  iconType="circle"
                  iconSize={8}
                  wrapperStyle={{ fontSize: 11 }}
                />
                <Area
                  type="monotone"
                  dataKey="completions"
                  stroke="#017bfe"
                  strokeWidth={2.5}
                  fill="url(#gComp)"
                  name="Completions"
                />
                <Area
                  type="monotone"
                  dataKey="participants"
                  stroke="#10b981"
                  strokeWidth={2.5}
                  fill="url(#gPart)"
                  name="Participants"
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>

          {/* Donut chart */}
          <div className="adm__panel adm__panel--donut">
            <div className="adm__panel-header">
              <div>
                <h3>Response Share</h3>
                <p>By assessment</p>
              </div>
            </div>
            <ResponsiveContainer width="100%" height={160}>
              <PieChart>
                <Pie
                  data={donutData}
                  cx="50%"
                  cy="50%"
                  innerRadius={46}
                  outerRadius={70}
                  paddingAngle={3}
                  dataKey="value"
                >
                  {donutData.map((_, i) => (
                    <Cell
                      key={i}
                      fill={DONUT_COLORS[i % DONUT_COLORS.length]}
                    />
                  ))}
                </Pie>
                <Tooltip formatter={(v, n) => [v, n]} />
              </PieChart>
            </ResponsiveContainer>
            <div className="adm__donut-legend">
              {donutData.map((d, i) => (
                <div key={i} className="adm__donut-item">
                  <span
                    className="adm__donut-dot"
                    style={{
                      background: DONUT_COLORS[i % DONUT_COLORS.length],
                    }}
                  />
                  <span className="adm__donut-name">{d.name}</span>
                  <strong>{d.value}</strong>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ── ASSESSMENTS LIST — compact row layout matching TestManagement ──── */}
      <section className="adm__list-section">
        <div className="adm__panel adm__panel--list">
          <div className="adm__panel-header">
            <div>
              <h3>Assessments</h3>
              <p>Full breakdown per assessment</p>
            </div>
          </div>

          {sortedTests.length > 0 ? (
            <div className="adm__list">
              <div className="adm__row adm__row--header">
                <div className="adm__row__title-cell">
                  <button
                    type="button"
                    className="sort-btn"
                    onClick={() => toggleSort("title")}
                  >
                    Assessment
                    {renderSortIcon("title")}
                  </button>
                </div>
                <div className="adm__row__category">
                  <span className="header-label">Category</span>
                </div>
                <div className="adm__row__duration">
                  <span className="header-label">Duration</span>
                </div>
                <div className="adm__row__attempts">
                  <span className="header-label">Attempts</span>
                </div>
                <div className="adm__row__participants">
                  <button
                    type="button"
                    className="sort-btn"
                    onClick={() => toggleSort("resultCount")}
                  >
                    Participants
                    {renderSortIcon("resultCount")}
                  </button>
                </div>
                <div className="adm__row__score">
                  <button
                    type="button"
                    className="sort-btn"
                    onClick={() => toggleSort("averageScore")}
                  >
                    Score
                    {renderSortIcon("averageScore")}
                  </button>
                </div>
                <div className="adm__row__status">
                  <span className="header-label">Status</span>
                </div>
                <div className="adm__row__expires">
                  <button
                    type="button"
                    className="sort-btn"
                    onClick={() => toggleSort("expirationDate")}
                  >
                    Expires
                    {renderSortIcon("expirationDate")}
                  </button>
                </div>
              </div>

              {sortedTests.map((t) => {
                const isHighlighted =
                  String(t.testId) === String(highlightedTestId);
                return (
                  <div
                    key={t.testId}
                    ref={(el) => (rowRefs.current[t.testId] = el)}
                    className={`adm__row ${isHighlighted ? "adm__row--highlighted" : ""}`}
                  >
                    <div className="adm__row__title-cell">
                      <div className="adm__row__icon">
                        <FileText size={14} />
                      </div>
                      <div className="adm__row__title-content">
                        <h4 className="adm__test-name">{t.title}</h4>
                        <p className="adm__test-desc">
                          {t.description
                            ? `${t.description.slice(0, 60)}…`
                            : `Test ID: ${t.testId}`}
                        </p>
                      </div>
                    </div>

                    <div className="adm__row__category" data-label="Category">
                      <span className="category-badge">
                        <Tag size={11} />
                        {t.testCategoryType}
                      </span>
                    </div>

                    <div className="adm__row__duration" data-label="Duration">
                      <span className="meta-item">
                        <Clock size={12} />
                        {formatDuration(t.testDuration)}
                      </span>
                    </div>

                    <div className="adm__row__attempts" data-label="Attempts">
                      <span className="meta-item">
                        <Repeat size={12} />
                        {formatAttempts(t)}
                      </span>
                      {t.usedAttempts !== null && (
                        <span className="adm__attempts-used">
                          {t.usedAttempts} used
                        </span>
                      )}
                    </div>

                    <div
                      className="adm__row__participants"
                      data-label="Participants"
                    >
                      <span className="adm__count-badge">{t.resultCount}</span>
                    </div>

                    <div className="adm__row__score" data-label="Score">
                      <div className="adm__score-main">
                        {parseFloat((t.averageScore || 0).toFixed(1))} /{" "}
                        {t.totalScore}
                      </div>
                      <div className="adm__score-range">
                        <span className="adm__score-hi">
                          <ArrowUp size={10} />
                          {t.highestScore ?? "—"}
                        </span>
                        <span className="adm__score-lo">
                          <ArrowDown size={10} />
                          {t.lowestScore ?? "—"}
                        </span>
                      </div>
                      <ScoreBar
                        value={t.averageScore || 0}
                        max={t.totalScore || 1}
                      />
                    </div>

                    <div className="adm__row__status">
                      <StatusBadge status={t.status} />
                    </div>

                    <div className="adm__row__expires" data-label="Expires">
                      <span className="date-cell">
                        {t.expirationDate
                          ? new Date(t.expirationDate).toLocaleDateString(
                              "en-US",
                              {
                                month: "short",
                                day: "numeric",
                                year: "2-digit",
                              },
                            )
                          : "—"}
                      </span>

                      {t.isExpiringSoon && (
                        <span className="adm__days-left adm__days-left--warning">
                          {t.daysLeft}d left
                        </span>
                      )}

                      {!t.isExpired &&
                        !t.isExpiringSoon &&
                        t.daysLeft !== null && (
                          <span className="adm__days-left">
                            {t.daysLeft}d left
                          </span>
                        )}
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="adm__empty-state">
              <FileText size={40} />
              <h3>No assessments found</h3>
              <p>Create your first assessment to see analytics here.</p>
            </div>
          )}
        </div>
      </section>

      {/* ── SCORE DISTRIBUTION ───────────────────────────────────────────────── */}
      {!isUserOnly && (
        <section className="adm__dist-row">
          <div className="adm__panel adm__panel--distribution">
            <div className="adm__panel-header">
              <div>
                <h3>Score Distribution</h3>
                <p>Avg · Highest · Lowest per assessment</p>
              </div>
            </div>
            <ResponsiveContainer width="100%" height={190}>
              <BarChart
                data={tests.map((t) => ({
                  name:
                    t.title.length > 14 ? t.title.slice(0, 13) + "…" : t.title,
                  avg: parseFloat((t.averageScore || 0).toFixed(1)),
                  high: t.highestScore || 0,
                  low: t.lowestScore || 0,
                }))}
                margin={{ top: 8, right: 10, bottom: 0, left: -20 }}
                barSize={10}
                barGap={2}
              >
                <CartesianGrid
                  strokeDasharray="3 3"
                  stroke="rgba(0,0,0,0.05)"
                />
                <XAxis
                  dataKey="name"
                  tick={{ fontSize: 11, fill: "#94a3b8" }}
                  axisLine={false}
                  tickLine={false}
                />
                <YAxis
                  tick={{ fontSize: 11, fill: "#94a3b8" }}
                  axisLine={false}
                  tickLine={false}
                />
                <Tooltip content={<CustomTooltip />} />
                <Legend
                  iconType="circle"
                  iconSize={8}
                  wrapperStyle={{ fontSize: 11 }}
                />
                <Bar
                  dataKey="avg"
                  fill="#017bfe"
                  radius={[4, 4, 0, 0]}
                  name="Average"
                />
                <Bar
                  dataKey="high"
                  fill="#10b981"
                  radius={[4, 4, 0, 0]}
                  name="Highest"
                />
                <Bar
                  dataKey="low"
                  fill="#e11d48"
                  radius={[4, 4, 0, 0]}
                  name="Lowest"
                />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </section>
      )}
    </div>
  );
};

export default Admin;
